// Copyright © 2026 Alex Bruce. All rights reserved.
//
// Org cost-code catalog editor (Settings → Libraries → Cost Codes).
//
// Reads and writes org_cost_codes directly. RLS restricts writes to
// owners/admins; this component also resolves the caller's role so the UI
// can present itself as read-only rather than letting a member make edits
// that the database will silently reject.

import React, { useState, useEffect, useCallback, useRef } from "react";
import type { Theme } from "../lib/theme";
import { supabase } from "../lib/supabase";
import { ALL_DIVISIONS } from "../projectData";
import { buildImportPlan, type ImportPlan } from "../lib/costCodeImport";
import * as Icon from "./OEIcons";
import type { ShowToast } from "../types";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

interface Row { id: string; div_id: string; cost_code: string; name: string; sort_order: number; }

interface Props {
  T: Theme;
  isMobile: boolean;
  isTablet: boolean;
  userOrgs: Array<{ id: string; name: string }>;
  currentOrgId: string | null;
  showToast: ShowToast;
}

export default function CostCodeLibrary({ T, isMobile, isTablet, userOrgs, currentOrgId, showToast }: Props) {
  const [orgId, setOrgId] = useState<string | null>(currentOrgId || userOrgs[0]?.id || null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [addingDiv, setAddingDiv] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ code: string; name: string }>({ code: "", name: "" });
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // ── Load catalog + caller's role for the selected org ────────────────
  const load = useCallback(async () => {
    if (!orgId || !supabase) { setRows([]); return; }
    setLoading(true);
    // The role lookup MUST filter by user as well as org. Without the
    // user_id it asked "what is a role in this org", and maybeSingle() errors
    // the moment an org has two members -- so roleData came back null and
    // every member including the owner was shown a read-only catalog. It
    // worked only while an org had exactly one person in it.
    const { data: { user } } = await supabase.auth.getUser();
    const [{ data: codeData, error: codeErr }, { data: roleData }] = await Promise.all([
      supabase.from("org_cost_codes").select("id, div_id, cost_code, name, sort_order")
        .eq("org_id", orgId).order("div_id").order("sort_order"),
      user
        ? supabase.from("org_members").select("role")
            .eq("org_id", orgId).eq("user_id", user.id).maybeSingle()
        : Promise.resolve({ data: null }),
    ]);
    setLoading(false);
    if (codeErr) { showToast("Couldn't load cost codes"); return; }
    setRows((codeData || []) as Row[]);
    setCanEdit(["owner", "admin"].includes((roleData as any)?.role || ""));
  }, [orgId, showToast]);

  useEffect(() => { load(); }, [load]);

  // ── Mutations ────────────────────────────────────────────────────────
  // Each writes through to Supabase and reconciles local state on success,
  // so a rejected write never leaves the UI showing a change that didn't land.
  const updateRow = async (id: string, patch: Partial<Pick<Row, "cost_code" | "name">>) => {
    const prev = rows;
    setRows(r => r.map(x => x.id === id ? { ...x, ...patch } : x));
    const { error } = await supabase!.from("org_cost_codes").update(patch).eq("id", id);
    if (error) { setRows(prev); showToast(error.message.includes("duplicate") ? "That cost code already exists in this division" : "Change not saved"); }
  };

  const deleteRow = async (row: Row) => {
    if (!window.confirm(`Remove "${row.name}" (${row.cost_code}) from your organization's standard catalog?\n\nExisting estimates keep their subcategories — this only affects the picker and future projects.`)) return;
    const prev = rows;
    setRows(r => r.filter(x => x.id !== row.id));
    const { error } = await supabase!.from("org_cost_codes").delete().eq("id", row.id);
    if (error) { setRows(prev); showToast("Couldn't remove entry"); }
  };

  const addRow = async (divId: string) => {
    const code = draft.code.trim(), name = draft.name.trim();
    if (!code || !name) { showToast("Enter both a cost code and a name"); return; }
    const maxSort = Math.max(0, ...rows.filter(r => r.div_id === divId).map(r => r.sort_order));
    const { data, error } = await supabase!.from("org_cost_codes")
      .insert({ org_id: orgId, div_id: divId, cost_code: code, name, sort_order: maxSort + 10 })
      .select("id, div_id, cost_code, name, sort_order").single();
    if (error) { showToast(error.message.includes("duplicate") ? "That cost code already exists in this division" : "Couldn't add entry"); return; }
    setRows(r => [...r, data as Row]);
    setDraft({ code: "", name: "" });
    setAddingDiv(null);
  };

  // ── Import ───────────────────────────────────────────────────────────
  // Read CSV/XLSX into header-keyed rows, then hand off to the pure planner.
  // Nothing is written until the user confirms the preview — this is a
  // shared org standard, so a silent bulk overwrite would be hostile.
  const readFile = async (file: File): Promise<Record<string, string>[]> => {
    let matrix: string[][];
    if (file.name.toLowerCase().endsWith(".csv")) {
      const text = await file.text();
      matrix = text.split(/\r?\n/).filter(l => l.trim())
        .map(l => l.split(",").map(c => c.replace(/^"|"$/g, "").trim()));
    } else {
      const XLSX = await import("xlsx");
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      matrix = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, defval: "" }) as string[][];
    }
    if (matrix.length < 2) return [];
    const headers = (matrix[0] || []).map(h => String(h || "").trim().toLowerCase());
    return matrix.slice(1).map(cells => {
      const row: Record<string, string> = {};
      headers.forEach((h, j) => { row[h] = String(cells[j] ?? "").trim(); });
      return row;
    });
  };

  const onFile = async (file: File) => {
    try {
      const parsed = await readFile(file);
      if (parsed.length === 0) { showToast("That file has no data rows"); return; }
      setPlan(buildImportPlan(parsed, rows.map(r => ({ divId: r.div_id, costCode: r.cost_code, name: r.name }))));
    } catch {
      showToast("Couldn't read that file — try CSV or .xlsx");
    }
  };

  const applyPlan = async () => {
    if (!plan || !orgId) return;
    setImporting(true);
    const inserts = plan.toAdd.map((e, i) => ({
      org_id: orgId, div_id: e.divId, cost_code: e.costCode, name: e.name,
      sort_order: (Math.max(0, ...rows.filter(r => r.div_id === e.divId).map(r => r.sort_order)) + (i + 1) * 10),
    }));
    let failed = 0;
    if (inserts.length) {
      const { error } = await supabase!.from("org_cost_codes").insert(inserts);
      if (error) failed += inserts.length;
    }
    for (const u of plan.toUpdate) {
      const row = rows.find(r => r.div_id === u.divId && r.cost_code === u.costCode);
      if (!row) continue;
      const { error } = await supabase!.from("org_cost_codes").update({ name: u.name }).eq("id", row.id);
      if (error) failed++;
    }
    setImporting(false);
    setPlan(null);
    await load();
    showToast(failed
      ? `Imported with ${failed} failure${failed === 1 ? "" : "s"} — check your permissions`
      : `Imported ${plan.toAdd.length} new · ${plan.toUpdate.length} renamed`);
  };

  const pad = isMobile ? "16px" : isTablet ? "24px 28px" : "36px 44px";

  if (!supabase || userOrgs.length === 0) {
    return (
      <div style={{ padding: pad, maxWidth: 640 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: T.textSecondary, marginBottom: 8 }}>Cost Codes</div>
        <div style={{ fontSize: 12, color: T.textWeak, lineHeight: 1.6 }}>
          Cost codes are shared across an organization. Create or join an organization under
          Settings → Members &amp; Roles to define your standard catalog.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: pad, maxWidth: 720 }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: T.textSecondary, marginBottom: 8 }}>Cost Codes</div>
      <div style={{ fontSize: 12, color: T.textWeak, marginBottom: 18, lineHeight: 1.6 }}>
        Your organization's standard subcategories. These populate the <strong style={{ color: T.textMuted }}>+ Add Subcategory</strong> picker
        in every estimate and define which code/name pairings are protected from accidental edits.
        Changes here don't alter existing estimates.
      </div>

      {/* Org selector — only meaningful with more than one org */}
      {userOrgs.length > 1 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", textTransform: "uppercase" as any }}>Organization</span>
          <select value={orgId || ""} onChange={e => setOrgId(e.target.value)}
            style={{ background: T.bgInput, border: `1px solid ${T.borderSecondary}`, color: T.textSecondary, fontSize: 12, padding: "5px 8px", borderRadius: 3, fontFamily: FONT }}>
            {userOrgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
      )}

      {canEdit && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: "none" }}
            onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }} />
          <button onClick={() => fileRef.current?.click()}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: `1px solid ${T.borderSecondary}`,
              color: T.textSecondary, fontSize: 11, fontWeight: 600, padding: "5px 12px", borderRadius: 3, cursor: "pointer", fontFamily: FONT }}>
            <Icon.Upload size={12}/> Import CSV / Excel
          </button>
          <span style={{ fontSize: 10, color: T.textWeak }}>
            Needs a cost code and a name column. Division is read from a column or the code prefix.
          </span>
        </div>
      )}

      {/* Import preview — nothing is written until this is confirmed. */}
      {plan && (
        <div style={{ position: "fixed", inset: 0, background: "#0008", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={() => !importing && setPlan(null)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: T.bgPanel, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, maxWidth: 560, width: "100%", maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: `0 12px 40px #000a` }}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.borderPrimary}` }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.textSecondary }}>Review import</div>
              <div style={{ fontSize: 11, color: T.textWeak, marginTop: 4 }}>
                {plan.toAdd.length} to add · {plan.toUpdate.length} to rename · {plan.unchanged.length} unchanged
                {plan.skipped.length > 0 && <> · <span style={{ color: T.negative }}>{plan.skipped.length} skipped</span></>}
              </div>
            </div>
            <div style={{ overflowY: "auto", padding: "12px 20px", flex: 1 }}>
              {plan.toAdd.length === 0 && plan.toUpdate.length === 0 && (
                <div style={{ fontSize: 12, color: T.textWeak, padding: "8px 0" }}>
                  Nothing to change — everything in this file already matches your catalog.
                </div>
              )}
              {plan.toAdd.slice(0, 40).map((e, i) => (
                <div key={`a${i}`} style={{ display: "flex", gap: 10, fontSize: 11, padding: "3px 0", alignItems: "center" }}>
                  <span style={{ color: T.positive, fontWeight: 700, width: 14 }}>+</span>
                  <span style={{ fontFamily: "monospace", color: T.textTertiary, minWidth: 60 }}>{e.costCode}</span>
                  <span style={{ color: T.textSecondary, flex: 1 }}>{e.name}</span>
                  <span style={{ color: T.textWeak, fontSize: 10 }}>{ALL_DIVISIONS.find(d => d.id === e.divId)?.code}</span>
                </div>
              ))}
              {plan.toUpdate.slice(0, 40).map((e, i) => (
                <div key={`u${i}`} style={{ display: "flex", gap: 10, fontSize: 11, padding: "3px 0", alignItems: "center" }}>
                  <span style={{ color: "#d4a44a", fontWeight: 700, width: 14 }}>~</span>
                  <span style={{ fontFamily: "monospace", color: T.textTertiary, minWidth: 60 }}>{e.costCode}</span>
                  <span style={{ color: T.textWeak, flex: 1 }}>
                    <s>{e.oldName}</s> <span style={{ color: T.textSecondary }}>→ {e.name}</span>
                  </span>
                  <span style={{ color: T.textWeak, fontSize: 10 }}>{ALL_DIVISIONS.find(d => d.id === e.divId)?.code}</span>
                </div>
              ))}
              {plan.skipped.length > 0 && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.borderPrimary}` }}>
                  <div style={{ fontSize: 10, color: T.negative, fontWeight: 700, textTransform: "uppercase" as any, letterSpacing: ".08em", marginBottom: 6 }}>Skipped</div>
                  {plan.skipped.slice(0, 20).map((s, i) => (
                    <div key={`s${i}`} style={{ fontSize: 10, color: T.textWeak, padding: "2px 0" }}>
                      {s.row > 0 && <span style={{ marginRight: 6 }}>Row {s.row}</span>}
                      <span style={{ color: T.textTertiary }}>{s.raw}</span> — {s.reason}
                    </div>
                  ))}
                  {plan.skipped.length > 20 && <div style={{ fontSize: 10, color: T.textWeak, marginTop: 4 }}>+{plan.skipped.length - 20} more</div>}
                </div>
              )}
            </div>
            <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.borderPrimary}`, display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setPlan(null)} disabled={importing}
                style={{ background: "none", border: `1px solid ${T.borderSecondary}`, color: T.textWeak, fontSize: 11, padding: "6px 14px", borderRadius: 3, cursor: "pointer", fontFamily: FONT }}>
                Cancel
              </button>
              <button onClick={applyPlan} disabled={importing || (plan.toAdd.length === 0 && plan.toUpdate.length === 0)}
                style={{ background: T.borderAccent, border: "none", color: "#0f1117", fontSize: 11, fontWeight: 700, padding: "6px 16px", borderRadius: 3,
                  cursor: importing ? "default" : "pointer", fontFamily: FONT, opacity: (plan.toAdd.length === 0 && plan.toUpdate.length === 0) ? .4 : 1 }}>
                {importing ? "Importing…" : "Apply import"}
              </button>
            </div>
          </div>
        </div>
      )}

      {!canEdit && !loading && (
        <div style={{ fontSize: 11, color: T.textWeak, background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: 6, padding: "8px 12px", marginBottom: 16 }}>
          Read-only — only organization owners and admins can change the standard catalog.
        </div>
      )}

      {loading && <div style={{ fontSize: 12, color: T.textWeak }}>Loading…</div>}

      {!loading && ALL_DIVISIONS.map(div => {
        const divRows = rows.filter(r => r.div_id === div.id);
        const isAdding = addingDiv === div.id;
        // Hide untouched empty divisions so the page isn't 24 empty headings,
        // but keep them reachable while adding.
        if (divRows.length === 0 && !isAdding) {
          return (
            <div key={div.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, opacity: .45 }}>
              <span style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".08em", minWidth: 190 }}>{div.code} — {div.name}</span>
              {canEdit && (
                <button onClick={() => { setAddingDiv(div.id); setDraft({ code: "", name: "" }); }}
                  style={{ background: "none", border: "none", color: T.textWeak, fontSize: 10, cursor: "pointer", fontFamily: FONT, padding: 0 }}>
                  + add
                </button>
              )}
            </div>
          );
        }
        return (
          <div key={div.id} style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 10, color: T.borderAccent, letterSpacing: ".1em", marginBottom: 6, textTransform: "uppercase" as any, fontWeight: 600 }}>
              {div.code} — {div.name}
            </div>
            {divRows.map(row => (
              <div key={row.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, paddingLeft: 8 }}>
                <input defaultValue={row.cost_code} disabled={!canEdit}
                  onBlur={e => { const v = e.target.value.trim(); if (v && v !== row.cost_code) updateRow(row.id, { cost_code: v }); }}
                  style={{ background: T.bgApp, border: `1px solid ${T.borderPrimary}`, color: T.borderAccent, fontSize: 11, padding: "3px 8px", borderRadius: 3, width: 78, fontFamily: FONT, letterSpacing: ".06em" }} />
                <input defaultValue={row.name} disabled={!canEdit}
                  onBlur={e => { const v = e.target.value.trim(); if (v && v !== row.name) updateRow(row.id, { name: v }); }}
                  style={{ background: T.bgApp, border: `1px solid ${T.borderPrimary}`, color: T.textSecondary, fontSize: 11, padding: "3px 8px", borderRadius: 3, flex: 1, fontFamily: FONT }} />
                {canEdit && (
                  <button onClick={() => deleteRow(row)} title="Remove from catalog"
                    style={{ background: "none", border: "none", color: T.textWeak, cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 4px", opacity: .5, flexShrink: 0 }}
                    onMouseEnter={e => (e.currentTarget.style.opacity = "1")}
                    onMouseLeave={e => (e.currentTarget.style.opacity = "0.5")}>&times;</button>
                )}
              </div>
            ))}
            {isAdding ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, paddingLeft: 8 }}>
                <input autoFocus value={draft.code} onChange={e => setDraft(d => ({ ...d, code: e.target.value }))} placeholder="00-000"
                  style={{ background: T.bgApp, border: `1px solid ${T.borderAccent}66`, color: T.borderAccent, fontSize: 11, padding: "3px 8px", borderRadius: 3, width: 78, fontFamily: FONT, letterSpacing: ".06em" }} />
                <input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} placeholder="Subcategory name"
                  onKeyDown={e => { if (e.key === "Enter") addRow(div.id); if (e.key === "Escape") setAddingDiv(null); }}
                  style={{ background: T.bgApp, border: `1px solid ${T.borderAccent}66`, color: T.textSecondary, fontSize: 11, padding: "3px 8px", borderRadius: 3, flex: 1, fontFamily: FONT }} />
                <button onClick={() => addRow(div.id)}
                  style={{ background: T.borderAccent, border: "none", color: "#0f1117", fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 3, cursor: "pointer", fontFamily: FONT }}>Add</button>
                <button onClick={() => setAddingDiv(null)}
                  style={{ background: "none", border: "none", color: T.textWeak, fontSize: 10, cursor: "pointer", fontFamily: FONT }}>Cancel</button>
              </div>
            ) : canEdit && (
              <button onClick={() => { setAddingDiv(div.id); setDraft({ code: "", name: "" }); }}
                style={{ background: "none", border: `1px dashed ${T.borderSecondary}`, color: T.textWeak, fontSize: 10, padding: "3px 10px", borderRadius: 3, cursor: "pointer", fontFamily: FONT, marginLeft: 8 }}>
                + Add to {div.code}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
